import { forwardRef, MouseEvent, Ref, useEffect, useState } from 'react';

import { faCalendarDay, faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import {
  eachWeekOfInterval,
  endOfMonth,
  getISOWeek,
  isValid,
  set,
  startOfMonth,
  startOfToday
} from 'date-fns';
import { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { formatByLocale, getLocale, getMaxDate, getMinDate } from 'utils/date';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import {
  CalendarPicker,
  DatePicker,
  MonthPicker,
  YearPicker
} from '@mui/x-date-pickers-pro';

import { FontAwesomeSvgIcon } from 'components/shared';

const maskMap = {
  en: '__/__/____',
  nl: '__-__-____'
};

const DatePickerStyles = styled.div`
  position: relative;
  padding-bottom: 10px;

  .header {
    position: absolute;
    height: 30px;
    margin-top: 11px;
    margin-bottom: 8px;
    padding-left: 19px;

    button {
      text-transform: none;
      color: inherit;
      font-size: 16px;
    }
  }

  &.month-year-view {
    .header {
      position: static;
    }
  }

  .month-weeks {
    position: absolute;
    left: 0;
    top: 94px;
    width: 25px;
    background-color: #f7f7f7;
    border-right: 1px solid #ccc;

    > div {
      line-height: 36px;
      margin: 2px 0;
      text-align: center;
    }
  }

  .calendar-picker {
    padding-left: 25px;

    > div:first-child {
      justify-content: flex-end;

      > div:first-child {
        display: none;
      }
    }
  }
`;

type Props = {
  date?: Date | null;
  showInput?: boolean;
  label?: string;
  minDate?: Date;
  maxDate?: Date;
  open?: boolean;
  readOnly?: boolean;
  monthYearView?: boolean;
  required?: true;
  disabled?: boolean;
  onChange: (date: Date | null) => void;
  error?: FieldError;
};

const bottomDate = getMinDate();
const topDate = getMaxDate();

export default forwardRef(
  (props: Props, ref: Ref<HTMLDivElement> | undefined) => {
    const {
      date: _date,
      showInput,
      label,
      minDate: _minDate,
      maxDate: _maxDate,
      open: _open,
      readOnly,
      monthYearView,
      required,
      onChange,
      disabled,
      error
    } = props;
    const date = _date || startOfToday();
    const minDate = _minDate || bottomDate;
    const maxDate = _maxDate || topDate;
    const { t } = useTranslation('common');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [view, setView] = useState<'day' | 'month' | 'year'>(
      monthYearView ? 'month' : 'day'
    );
    const [open, setOpen] = useState(_open || false);
    const [currentDateInView, setCurrentDateInView] = useState(date);

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
      setView(monthYearView ? 'month' : 'day');
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const onYearViewRender = () => {
      const selectedYearButton = document.querySelector(
        '.PrivatePickersYear-yearButton.Mui-selected'
      );
      if (selectedYearButton) {
        selectedYearButton.scrollIntoView({
          block: 'center'
        });
      }
    };

    const onMonthChange = (date?: Date | null) => {
      if (date && date.getMonth() !== _date?.getMonth()) {
        onChange(set(date, { month: date.getMonth() }));
        if (monthYearView) {
          handleClose();
        } else {
          setView('day');
        }
      }
    };

    const onYearChange = (date?: Date | null) => {
      if (date) {
        onChange(set(date, { year: date.getFullYear() }));
        setView('month');
      }
    };

    const getMonthWeeks = () =>
      isValid(currentDateInView)
        ? eachWeekOfInterval(
            {
              start: startOfMonth(currentDateInView),
              end: endOfMonth(currentDateInView)
            },
            { weekStartsOn: 1 }
          )
        : [];

    useEffect(() => {
      if (_date) {
        setCurrentDateInView(_date);
      }
    }, [_date]);

    return (
      <>
        <div className="date-picker-wrapper d-flex v-bottom">
          {showInput && (
            <DatePicker
              onChange={(newDate) => onChange(newDate)}
              disableOpenPicker
              mask={maskMap[getLocale()]}
              minDate={minDate}
              maxDate={maxDate}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  required={required}
                  error={!!error}
                  helperText={error?.message ?? null}
                />
              )}
              value={date}
              label={label}
              open={open}
              onClose={() => setOpen(false)}
              readOnly={readOnly}
              disabled={disabled}
              ref={ref}
            />
          )}
          {!readOnly && (
            <Tooltip title={t('pickDate') || ''} arrow>
              <IconButton
                color="primary"
                onClick={handleOpen}
                size="small"
                disabled={disabled}
              >
                <FontAwesomeSvgIcon icon={faCalendarDay} fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setCurrentDateInView(date);
            handleClose();
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          role="dialog"
        >
          <DatePickerStyles
            className={classNames({ 'month-year-view': monthYearView })}
          >
            {(view === 'day' || monthYearView) && (
              <Zoom in>
                <div className="header">
                  <Button
                    variant="text"
                    onClick={() => setView('month')}
                    size="small"
                  >
                    {isValid(currentDateInView)
                      ? formatByLocale(currentDateInView, 'MMMM')
                      : ''}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setView('year')}
                    size="small"
                  >
                    {isValid(currentDateInView)
                      ? formatByLocale(currentDateInView, 'yyyy')
                      : ''}
                  </Button>
                </div>
              </Zoom>
            )}
            {view === 'day' && (
              <Zoom in>
                <Box sx={{ display: 'flex', width: 320 }}>
                  <div className="month-weeks">
                    {getMonthWeeks().map((date) => (
                      <div key={+date}>
                        <button
                          type="button"
                          className="clean-btn"
                          onClick={() => {
                            onChange(date);
                            handleClose();
                          }}
                        >
                          {getISOWeek(date)}
                        </button>
                      </div>
                    ))}
                  </div>
                  <CalendarPicker
                    onChange={(date) => {
                      if (date) {
                        onChange(date);
                        handleClose();
                      }
                    }}
                    className="calendar-picker"
                    date={date}
                    onMonthChange={setCurrentDateInView}
                    showDaysOutsideCurrentMonth
                    view="day"
                    shouldDisableDate={(date) =>
                      date < minDate || date > maxDate
                    }
                  />
                </Box>
              </Zoom>
            )}
            {view === 'month' && (
              <Zoom in>
                <Box sx={{ display: 'flex', width: 300, padding: '0 10px' }}>
                  <MonthPicker
                    date={date}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={onMonthChange}
                  />
                </Box>
              </Zoom>
            )}
            {view === 'year' && (
              <Fade in onEnter={onYearViewRender}>
                <Box sx={{ display: 'flex', width: 320, height: 320 }}>
                  <YearPicker
                    date={date}
                    shouldDisableYear={() => false}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={onYearChange}
                  />
                </Box>
              </Fade>
            )}
            <div
              className="d-flex h-center"
              style={view === 'day' ? undefined : { paddingTop: 20 }}
            >
              {(monthYearView || view === 'day') && (
                <Zoom in>
                  <Button
                    onClick={() => {
                      onChange(startOfToday());
                      handleClose();
                    }}
                    variant="text"
                  >
                    {t('today')}
                  </Button>
                </Zoom>
              )}
              {!monthYearView && view !== 'day' && (
                <Zoom in>
                  <Button
                    onClick={() => setView('day')}
                    variant="text"
                    startIcon={<FontAwesomeSvgIcon icon={faChevronLeft} />}
                  >
                    {t('back')}
                  </Button>
                </Zoom>
              )}
            </div>
          </DatePickerStyles>
        </Popover>
      </>
    );
  }
);
